import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import {
  TrendingUp,
  LocationOn,
  Business,
  Person,
  Euro,
  Download,
  Map,
  Analytics,
  Assessment,
} from '@mui/icons-material';
import { DataService } from '../services/dataService';
import { GeographicData, ChartData } from '../types';

const AnalyticsScreen: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'month' | 'quarter' | 'year'>('month');
  const [metricType, setMetricType] = useState<'leads' | 'conversions' | 'revenue'>('leads');

  const { data: geographicData, isLoading: geoLoading, error: geoError } = useQuery<GeographicData[]>({
    queryKey: ['geographicData'],
    queryFn: DataService.getGeographicData,
  });

  const { data: chartData, isLoading: chartLoading, error: chartError } = useQuery<ChartData>({
    queryKey: ['chartData'],
    queryFn: DataService.getChartData,
  });

  const handleTimeRangeChange = (event: SelectChangeEvent) => {
    setTimeRange(event.target.value as 'month' | 'quarter' | 'year');
  };

  const handleMetricTypeChange = (event: SelectChangeEvent) => {
    setMetricType(event.target.value as 'leads' | 'conversions' | 'revenue');
  };

  const handleExportData = () => {
    if (!geographicData || geographicData.length === 0) {
      alert('Aucune donnée à exporter');
      return;
    }

    try {
      // Create CSV content
      const headers = [
        'Région',
        'Leads',
        'Conversions',
        'Taux de conversion (%)',
        'Revenus (€)',
        'Agences'
      ];

      const csvContent = [
        headers.join(','),
        ...geographicData.map(item => [
          item.region,
          item.leads,
          item.conversions,
          item.conversionRate.toFixed(2),
          item.revenue.toFixed(2),
          item.agencies
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `analytics_geographic_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
      
      alert(`Export réussi ! ${geographicData.length} régions exportées.`);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      alert('Erreur lors de l\'export. Veuillez réessayer.');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (geoLoading || chartLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Chargement des analyses...</Typography>
      </Box>
    );
  }

  if (geoError || chartError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Erreur lors du chargement des données d'analyse</Alert>
      </Box>
    );
  }

  // Check if we have real data to display
  const hasGeographicData = geographicData && geographicData.length > 0;
  const hasChartData = chartData && (
    (chartData.conversionFunnel && chartData.conversionFunnel.length > 0) ||
    (chartData.topSponsors && chartData.topSponsors.length > 0) ||
    (chartData.topAgencies && chartData.topAgencies.length > 0)
  );

  if (!hasGeographicData && !hasChartData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Analyses avancées</Typography>
        <Alert severity="info">
          Aucune donnée disponible pour l'analyse. Les données apparaîtront une fois que des leads et conversions seront enregistrés dans le système.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Analyses avancées</Typography>
        {hasGeographicData && (
          <Button variant="outlined" startIcon={<Download />} onClick={handleExportData}>
            Exporter données
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, alignItems: 'center' }}>
          <FormControl fullWidth>
            <InputLabel>Période</InputLabel>
            <Select
              value={timeRange}
              label="Période"
              onChange={handleTimeRangeChange}
            >
              <MenuItem value="month">Ce mois</MenuItem>
              <MenuItem value="quarter">Ce trimestre</MenuItem>
              <MenuItem value="year">Cette année</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Métrique</InputLabel>
            <Select
              value={metricType}
              label="Métrique"
              onChange={handleMetricTypeChange}
            >
              <MenuItem value="leads">Leads</MenuItem>
              <MenuItem value="conversions">Conversions</MenuItem>
              <MenuItem value="revenue">Revenus</MenuItem>
            </Select>
          </FormControl>
          <Chip 
            label={`${timeRange === 'month' ? 'Mensuel' : timeRange === 'quarter' ? 'Trimestriel' : 'Annuel'}`}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Paper>

      {/* Geographic Heatmap */}
      {hasGeographicData && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
              Carte de chaleur géographique
            </Typography>
            <Button variant="outlined" startIcon={<Map />}>
              Voir la carte
            </Button>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3 }}>
            {geographicData.map((region) => (
              <Card key={region.region} sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                },
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LocationOn color="primary" />
                    <Typography variant="h6" noWrap>
                      {region.region}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                    <Box>
                      <Typography variant="h4" color="primary.main">
                        {region.leads}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Leads
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h4" color="success.main">
                        {region.conversions}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Conversions
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Taux: {region.conversionRate.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {region.agencies} agences
                    </Typography>
                  </Box>
                  
                  <Typography variant="h6" color="success.main">
                    {formatCurrency(region.revenue)}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Paper>
      )}

      {/* Charts Section */}
      {hasChartData && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3, mb: 4 }}>
          {/* Conversion Funnel */}
          {chartData.conversionFunnel && chartData.conversionFunnel.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Répartition des leads
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData.conversionFunnel}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ stage, count }) => `${stage}: ${count}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {chartData.conversionFunnel.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Top Performers Summary */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Résumé des performances
            </Typography>
            
            {chartData.topSponsors && chartData.topSponsors.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Top parrain: {chartData.topSponsors[0]?.name}
                </Typography>
                <Typography variant="h6" color="primary">
                  {chartData.topSponsors[0]?.leads} leads générés
                </Typography>
              </Box>
            )}
            
            {chartData.topAgencies && chartData.topAgencies.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Top agence: {chartData.topAgencies[0]?.name}
                </Typography>
                <Typography variant="h6" color="success">
                  {chartData.topAgencies[0]?.leads} leads reçus
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Top Performers Comparison */}
      {hasChartData && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          {/* Top Sponsors */}
          {chartData.topSponsors && chartData.topSponsors.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                Top 5 des parrains
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData.topSponsors} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={80} />
                  <Tooltip />
                  <Bar dataKey="leads" fill="#2196F3" name="Leads" />
                  <Bar dataKey="conversion" fill="#4CAF50" name="Conversion %" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Top Agencies */}
          {chartData.topAgencies && chartData.topAgencies.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
                Top 5 des agences
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData.topAgencies} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={80} />
                  <Tooltip />
                  <Bar dataKey="leads" fill="#FF9800" name="Leads reçus" />
                  <Bar dataKey="mandates" fill="#9C27B0" name="Mandats" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}
        </Box>
      )}

      {/* Data Summary */}
      {hasGeographicData && (
        <Paper sx={{ p: 3, mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Résumé des données
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Total des régions
                </Typography>
                <Typography variant="h4" color="primary">
                  {geographicData.length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Régions avec activité
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Total des leads
                </Typography>
                <Typography variant="h4" color="success">
                  {geographicData.reduce((sum, region) => sum + region.leads, 0)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Leads générés
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Total des conversions
                </Typography>
                <Typography variant="h4" color="warning">
                  {geographicData.reduce((sum, region) => sum + region.conversions, 0)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Ventes réalisées
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default AnalyticsScreen;
